import axios from "axios";
import store from "@/store";
import router from "@/router";

// const API_BASE_URL = "https://api.trekko.xyz";
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

const axiosInstance = axios.create({
	baseURL: API_BASE_URL,
	withCredentials: true,
});

axiosInstance.interceptors.response.use(
	(response) => response,
	async (error) => {
		if (error.response?.status === 401) {
			// Logout user
			await store.dispatch("auth/logout");

			// Redirect to login
			router.push("/register");
		}
		return Promise.reject(error);
	},
);

const jsonHeaders = { "Content-Type": "application/json" };
const formHeaders = { "Content-Type": "multipart/form-data" };
export const httpService = {
	getAnalytics() {
		return axiosInstance.get("/dashboard").then((res) => res.data);
	},

	createEvent(event) {
		return axiosInstance.post("/events", event, { headers: formHeaders });
	},

	uploadEventImage(eventId, organizer_logo) {
		return axiosInstance
			.put(`/events/${eventId}/sponsors`, organizer_logo, {
				headers: formHeaders,
			})
			.then((res) => res.data);
	},

	deleteEventImage(eventId, logoId) {
		return axiosInstance
			.delete(`/events/${eventId}/sponsors/${logoId}/delete`, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},

	getEvents() {
		return axiosInstance.get("/admin/events").then((res) => res.data);
	},

	updateEvent(event) {
		return axiosInstance
			.put("/events/update", event, { headers: formHeaders })
			.then((res) => res.data);
	},

	getEvent(id) {
		return axiosInstance.get(`/admin/event/${id}`).then((res) => res.data);
	},

	getCategories() {
		return axiosInstance.get("/category").then((res) => res.data);
	},

	createCategory(category) {
		return axiosInstance
			.post("/category", category, { headers: jsonHeaders })
			.then((res) => res.data);
	},

	updateCategory(category) {
		return axiosInstance
			.put("/category/update", category)
			.then((res) => res.data);
	},

	createOrganization(organizer) {
		return axiosInstance
			.post("/organizations", organizer, { headers: formHeaders })
			.then((res) => res.data);
	},

	getOrganizations() {
		return axiosInstance.get("/organizations").then((res) => res.data);
	},

	updateOrganizer(organizer) {
		return axiosInstance
			.put("/organization/update", organizer)
			.then((res) => res.data);
	},

	getOrganizationAnalytics() {
		return axiosInstance.get(`/dashboard`).then((res) => res.data);
	},

	createUser(user, organizationId) {
		return axiosInstance
			.post(`/organizations/users/${organizationId}`, user, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},

	updateUser(user) {
		return axiosInstance.put("/user/update", user).then((res) => res.data);
	},
	checkEmailForAuth(data) {
		return axiosInstance
			.post("/check/email", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},

	requestOTP(data) {
		return axiosInstance
			.post("/user/request-otp", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},

	verifyOTP(data) {
		return axiosInstance
			.post("/user/verify-otp", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},

	getUserTicketData(data) {
		return axiosInstance
			.post("/ticket/data", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},

	validateTickets(data) {
		return axiosInstance
			.put("/ticket/validate", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	async checkAuth() {
		// Avoid refetching if already authenticated
		if (store.getters["auth/isAuthenticated"]) return true;
		return await store.dispatch("auth/fetchCurrentUser");
	},
	makeEventBookable(data, eventId) {
		return axiosInstance
			.put(`/events/${eventId}/bookable`, data, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	getRecommendedSong(eventId) {
		return axiosInstance
			.get(`events/${eventId}/music-suggestions`)
			.then((res) => res.data);
	},
	setReminder(eventId, payload) {
		return axiosInstance
			.post(`events/${eventId}/set-reminder`, payload, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	getRiminders(eventId) {
		return axiosInstance
			.get(`events/${eventId}/get-reminders`, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	disableReminder(eventId, reminderId) {
		return axiosInstance
			.put(`events/${eventId}/reminders/${reminderId}/disable`, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},
	editOrganization(id, organization) {
		return axiosInstance
			.put(`organizations/${id}/profile`, organization, {
				headers: formHeaders,
			})
			.then((res) => res.data);
	},
	generateOrganisationDomain(id, handle) {
		return axiosInstance
			.put(
				`organizations/${id}/updated/handle`,
				{ handle },
				{ headers: jsonHeaders },
			)
			.then((res) => res.data);
	},
	getUsers() {
		return axiosInstance
			.get(`/organizations/users`, {}, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	enableMusicRecommendation(data, eventId) {
		return axiosInstance
			.put(`/events/${eventId}/allow/music/recommendation`, data, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},
	enableUser(userId, option) {
		return axiosInstance
			.put(`/organizations/users/${userId}/${option}/activate`, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},
	disableUser(userId, option) {
		return axiosInstance
			.delete(`/organizations/users/${userId}/${option}/delete`, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},

	getOrganizers() {
		return axiosInstance
			.get(`/organizations`, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	deteleOrganizer(organizationId, option) {
		return axiosInstance
			.delete(`/organizations/users/${organizationId}/${option}/delete`, {
				headers: jsonHeaders,
			})
			.then((res) => res.data);
	},

	// News Letter Subscription
	sendNewsLetterSubscription(destination, data) {
		return axiosInstance
			.post(`/newsletter/${destination}/send`, data, { headers: jsonHeaders })
			.then((res) => res.data);
	},

	// Onboarding related APIs
	// Get current State
	getCurrentUserState() {
		return axiosInstance.get("/onboarding").then((res) => res.data);
	},
	// Move to next step
	moveToNextOnboardingStep() {
		return axiosInstance
			.post("/onboarding/next", { headers: jsonHeaders })
			.then((res) => res.data);
	},
	// Skip onboarding
	skipOnboarding() {
		return axiosInstance
			.post("/onboarding/skip", { headers: jsonHeaders })
			.then((res) => res.data);
	},
	//onboarding stripe connect
	connectStripe() {
		return axiosInstance
			.post("/organizations/stripe/connect", { headers: jsonHeaders })
			.then((res) => res.data);
	},
	clientSecretEmbedding(data) {
		return axiosInstance
			.post("/api/stripe/account-session", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	stripeStatus() {
		return axiosInstance
			.get("/organizations/stripe-status", { headers: jsonHeaders })
			.then((res) => res.data);
	},
	// payout history API
	getPayoutHistory() {
		return axiosInstance
			.get("/organizations/payout/history", { headers: jsonHeaders })
			.then((res) => res.data);
	},
	getPayoutDetails(payoutId) {
		return axiosInstance
			.get(`/organizations/payout/events/${payoutId}`, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	// Request payout API
	requestPayout(data) {
		return axiosInstance
			.post("organizations/payout", data, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	// retry request payout API
	retryRequestPayout(payoutId) {
		return axiosInstance
			.post(`organizations/payout/${payoutId}/retry`)
			.then((res) => res.data);
	},
	// Payout event list API
	getPayoutEventList() {
		return axiosInstance
			.get(`/organizations/payout/events`, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	// fetch attendee list for a specific event
	getEventAttendees(eventId) {
		return axiosInstance
			.get(`/attendees/${eventId}`, { headers: jsonHeaders })
			.then((res) => res.data);
	},
	updateAttendeeStatus(data, action) {
		return axiosInstance
			.post(
				`/attendees/${action}`, data, { headers: jsonHeaders },
			)
			.then((res) => res.data);
	}
	// async checkAuth() {
	// 	try {
	// 		const response = await axiosInstance.get("/auth/check");
	// 		return response.status === 200;
	// 	} catch {
	// 		return false;
	// 	}
	// },
};
